import type { IconType } from "react-icons";
import { NavIcons } from "./icons";

export type Tab =
  | "castle"
  | "army"
  | "inventory"
  | "alliance"
  | "marches"
  | "chat"
  | "quests";

interface Props {
  active: Tab;
  onChange: (tab: Tab) => void;
  // Tabs are gated on actually-built buildings + Castle level — pointing the
  // player at empty Army / Marches before there's anything to do there is
  // bad UX. Castle / Inventory / Задания are always visible.
  hasBarracks: boolean;
  hasWarHall: boolean;
  castleLevel: number;
  // Quests tab attention flag: true when there's something the player can
  // collect right now (a completed quest reward, a daily-quest chest, or
  // the newbie chest). Lights up the icon with a pulse + dot until the
  // player opens the tab and claims.
  questsHasAttention?: boolean;
}

// Alliance UI unlocks at Castle Lvl 10 — same threshold as beginner shield
// drop. Before that the player is still in the protected onboarding window.
const ALLIANCE_UNLOCK_LEVEL = 10;

interface TabDef {
  id: Tab;
  icon: IconType;
  label: string;
  isAvailable: (deps: { hasBarracks: boolean; hasWarHall: boolean; castleLevel: number }) => boolean;
}

const tabs: TabDef[] = [
  { id: "castle", icon: NavIcons.castle, label: "Замок", isAvailable: () => true },
  { id: "army", icon: NavIcons.army, label: "Армия", isAvailable: (d) => d.hasBarracks },
  { id: "inventory", icon: NavIcons.inventory, label: "Инвентарь", isAvailable: () => true },
  { id: "alliance", icon: NavIcons.alliance, label: "Альянс", isAvailable: (d) => d.castleLevel >= ALLIANCE_UNLOCK_LEVEL },
  { id: "marches", icon: NavIcons.marches, label: "Походы", isAvailable: (d) => d.hasWarHall },
  { id: "chat", icon: NavIcons.chat, label: "Чат", isAvailable: () => true },
  { id: "quests", icon: NavIcons.quests, label: "Задания", isAvailable: () => true },
];

// Static class names so Tailwind picks them up — can't be computed at runtime.
const GridColsByCount: Record<number, string> = {
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
};

export function BottomNav({ active, onChange, hasBarracks, hasWarHall, castleLevel, questsHasAttention }: Props) {
  const visible = tabs.filter((t) => t.isAvailable({ hasBarracks, hasWarHall, castleLevel }));
  const gridCols = GridColsByCount[visible.length] ?? "grid-cols-6";
  return (
    <nav
      data-tutorial="bottom-nav"
      className={`grid border-t border-tg-secondaryBg bg-tg-headerBg ${gridCols}`}
    >
      {visible.map((t) => {
        const isActive = t.id === active;
        const Icon = t.icon;
        const showAttention = t.id === "quests" && questsHasAttention && !isActive;
        return (
          <button
            key={t.id}
            type="button"
            data-tutorial={t.id === "quests" ? "quest-tab" : undefined}
            onClick={() => onChange(t.id)}
            className={`flex flex-col items-center gap-1 py-2 text-[10px] uppercase tracking-wide transition-colors ${
              isActive
                ? "text-tg-button"
                : showAttention
                  ? "text-tg-accent"
                  : "text-tg-hint"
            }`}
          >
            <span className="relative inline-flex">
              <Icon
                size={22}
                className={showAttention ? "animate-element-pulse" : undefined}
              />
              {showAttention && (
                <span className="pointer-events-none absolute -right-1.5 -top-1 flex h-2.5 w-2.5">
                  <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-tg-accent opacity-75" />
                  <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-tg-accent ring-2 ring-tg-headerBg" />
                </span>
              )}
            </span>
            <span>{t.label}</span>
          </button>
        );
      })}
    </nav>
  );
}
